// ===== routes/OAuthCallback.jsx =====

import React, { useEffect, useState }              from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useCompleteOAuthMutation }                from '../../redux/thunks/ai/completeoauth';
import { useCompleteGoogleLocationMutation }       from '../../redux/thunks/ai/completegooglelocation';
import {
  Box, Typography, CircularProgress,
  Button, Alert, Paper, Container
} from '@mui/material';
import {
  Error          as ErrorIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material';

import PageSelectionDialog      from '../ProviderProfile/FBInstaConnections/PageSelectionDialog';
import OAuthSuccessConfirmation from '../ProviderProfile/FBInstaConnections/OAuthSuccessConfirmation';
import GoogleLocationDialog     from '../ProviderProfile/GoogleLocationDialog';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();
  const { platform }    = useParams();

  const [status,       setStatus]       = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');

  const [showPageSelection, setShowPageSelection] = useState(false);
  const [sessionId,         setSessionId]         = useState(null);
  const [availablePages,    setAvailablePages]    = useState([]);

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationSessionId,  setLocationSessionId]  = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  const [completeOAuth]          = useCompleteOAuthMutation();
  const [completeGoogleLocation] = useCompleteGoogleLocationMutation();

  const clearSession = () => {
    ['oauth_platform', 'oauth_original_selection', 'oauth_business_id',
     'oauth_business_name', 'oauth_return_path', 'oauth_accepted_permissions']
      .forEach(k => sessionStorage.removeItem(k));
  };

  const getReturnPath = () => {
    const returnPath   = sessionStorage.getItem('oauth_return_path');
    const businessId   = sessionStorage.getItem('oauth_business_id');
    const businessName = sessionStorage.getItem('oauth_business_name');
    return returnPath
      || (businessId && businessName ? `/business/${businessId}/${businessName}` : null)
      || '/dashboard';
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('=== OAUTH CALLBACK PAGE ===');
        const params = {
          code:              searchParams.get('code'),
          state:             searchParams.get('state'),
          platform:          platform
                             || searchParams.get('platform')
                             || sessionStorage.getItem('oauth_platform'),
          error:             searchParams.get('error'),
          error_description: searchParams.get('error_description')
        };
        console.log('OAuth params:', params);

        if (params.error) {
          setStatus('error');
          setErrorMessage(params.error_description || `OAuth error: ${params.error}`);
          return;
        }

        if (!params.code || !params.state || !params.platform) {
          setStatus('error');
          setErrorMessage('Missing required OAuth parameters. Please try again.');
          return;
        }

        const result = await completeOAuth(params).unwrap();
        console.log('✅ OAUTH RESULT:', result);

        // ===== Facebook/Instagram — unchanged =====
        if (result.requiresPageSelection) {
          setSessionId(result.sessionId);
          setAvailablePages(result.availablePages || []);
          setShowPageSelection(true);
          setStatus('page_selection');
          return;
        }

        // ===== Google — location selection =====
        if (result.requiresLocationSelection) {
          setLocationSessionId(result.sessionId);
          setAvailableLocations(result.availableLocations || []);
          setShowLocationPicker(true);
          setStatus('location_selection');
          return;
        }

        // ===== Google — GBP pending (quota=0) =====
        // ← CHANGE: add platform: 'google' and gbpPending to connectionResult
        if (result.gbpPending) {
          setConnectionResult({
            ...result,
            platform:   'google',
            gbpPending: true,
            message:    'Google account connected. Business Profile access will activate once API approval is granted.',
          });
          setShowConfirmation(true);
          setStatus('confirmation');
          return;
        }

        // ===== Direct success =====
        if (result.success) {
          // ← CHANGE: add platform to connectionResult for all success cases
          setConnectionResult({
            ...result,
            platform: params.platform,
          });
          setShowConfirmation(true);
          setStatus('confirmation');
        } else {
          setStatus('error');
          setErrorMessage(result.message || 'Failed to complete account connection');
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setErrorMessage(
          error.data?.message ||
          error.message ||
          'Failed to connect account. Please try again.'
        );
      }
    };

    handleCallback();
  }, [searchParams, navigate, platform, completeOAuth]);

  const handlePageSelectionSuccess = (result) => {
    setShowPageSelection(false);
    setConnectionResult({
      ...result,
      platform: 'facebook',  // ← CHANGE: add platform
    });
    setShowConfirmation(true);
    setStatus('confirmation');
  };

  const handlePageSelectionCancel = () => {
    setShowPageSelection(false);
    setStatus('error');
    setErrorMessage('Page selection was cancelled. No accounts were connected.');
  };

  const handleLocationComplete = async (sessionId, locationName) => {
    try {
      const result = await completeGoogleLocation({
        sessionId,
        locationName
      }).unwrap();

      console.log('✅ Google location connected:', result);
      setShowLocationPicker(false);
      setConnectionResult({
        ...result,
        platform: 'google',  // ← CHANGE: add platform
        message:  `Google Business Profile "${result.accountName}" connected successfully`,
      });
      setShowConfirmation(true);
      setStatus('confirmation');
    } catch (error) {
      console.error('Google location selection failed:', error);
      setShowLocationPicker(false);
      setStatus('error');
      setErrorMessage(
        error.data?.message ||
        'Failed to connect Google Business Profile. Please try again.'
      );
    }
  };

  const handleLocationCancel = () => {
    setShowLocationPicker(false);
    setStatus('error');
    setErrorMessage('Location selection was cancelled.');
  };

  const handleContinueFromConfirmation = () => {
    const path = getReturnPath();
    clearSession();
    navigate(`${path}?oauth_success=true`, {
      state: {
        message: connectionResult?.message || 'Account connected successfully!',
        openTab: 'social'
      }
    });
  };

  const handleViewAccountsFromConfirmation = () => {
    const path = getReturnPath();
    clearSession();
    navigate(`${path}?oauth_success=true&scroll=social-accounts`);
  };

  const handleReturnToBusiness = () => {
    const path = getReturnPath();
    clearSession();
    navigate(path);
  };

  const handleRetry = () => {
    const path = getReturnPath();
    clearSession();
    navigate(path);
  };

  if (showPageSelection || showLocationPicker || showConfirmation) {
    return (
      <>
        <PageSelectionDialog
          open={showPageSelection}
          onClose={handlePageSelectionCancel}
          sessionId={sessionId}
          availablePages={availablePages}
          onSuccess={handlePageSelectionSuccess}
        />

        <GoogleLocationDialog
          open={showLocationPicker}
          onClose={handleLocationCancel}
          locations={availableLocations}
          sessionId={locationSessionId}
          onComplete={handleLocationComplete}
        />

        {/* ← CHANGE: pass platform prop */}
        <OAuthSuccessConfirmation
          open={showConfirmation}
          connectionResult={connectionResult}
          platform={connectionResult?.platform}
          onContinue={handleContinueFromConfirmation}
          onViewAccounts={handleViewAccountsFromConfirmation}
        />
      </>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        py:             4
      }}>
        <Paper elevation={3} sx={{
          p: 4, textAlign: 'center',
          borderRadius: 2, width: '100%', maxWidth: 400
        }}>

          {status === 'processing' && (
            <>
              <HourglassIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Connecting Your Account
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Please wait while we securely connect your account...
              </Typography>
              <CircularProgress size={40} />
            </>
          )}

          {(status === 'page_selection' || status === 'location_selection') && (
            <>
              <HourglassIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {status === 'location_selection'
                  ? 'Loading Your Business Locations'
                  : 'Loading Your Pages'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {status === 'location_selection'
                  ? 'Fetching your Google Business Profile locations...'
                  : 'Fetching your Facebook Pages and Instagram accounts...'}
              </Typography>
              <CircularProgress size={40} />
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="error.main">
                Connection Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">{errorMessage}</Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can try connecting again or return to your business profile.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleRetry}           sx={{ minWidth: 120 }}>Try Again</Button>
                <Button variant="outlined"  onClick={handleReturnToBusiness} sx={{ minWidth: 120 }}>Return to Profile</Button>
              </Box>
            </>
          )}
        </Paper>

        <Typography variant="caption" color="text.secondary"
          sx={{ mt: 3, textAlign: 'center', maxWidth: 400 }}>
          Having trouble? Make sure you have the necessary permissions
          and pop-ups are enabled in your browser.
        </Typography>
      </Box>
    </Container>
  );
};

export default OAuthCallback;